import {
    WORKBOOK_FIELD_CATALOG,
    catalogFieldsForBucket,
} from './workbookCatalog'
import type {
    WorkbookBucketConfig,
    WorkbookBucketId,
    WorkbookFieldConfig,
} from './workbook.types'

export const ADDRESS_COMPONENT_FIELD_IDS = [
    'current_address_line1',
    'current_address_city',
    'current_address_state',
    'current_address_zip',
] as const

export const ADDRESS_VIRTUAL_FIELD_ID = 'current_address'

export type CollectionState = 'skip' | 'optional' | 'required'

export interface BuilderFieldEntry {
    id: string
    label: string
    input_type: string
    fha_tier: 'safe' | 'caution' | 'prohibited'
    componentFieldIds?: readonly string[]
}

const ADDRESS_BUILDER_FIELD: BuilderFieldEntry = {
    id: ADDRESS_VIRTUAL_FIELD_ID,
    label: 'Address',
    input_type: 'address',
    fha_tier: 'safe',
    componentFieldIds: ADDRESS_COMPONENT_FIELD_IDS,
}

export function toCollectionState(
    enabled: boolean,
    required: boolean,
): CollectionState {
    if (!enabled) return 'skip'
    if (required) return 'required'
    return 'optional'
}

export function fromCollectionState(
    state: CollectionState,
): {
    enabled: boolean
    required: boolean
} {
    switch (state) {
        case 'skip':
            return { enabled: false, required: false }
        case 'optional':
            return { enabled: true, required: false }
        case 'required':
            return { enabled: true, required: true }
    }
}

export function getAddressCollectionState(
    fields: WorkbookFieldConfig[],
): CollectionState {
    const components = ADDRESS_COMPONENT_FIELD_IDS.map((fieldId) =>
        fields.find((field) => field.field_id === fieldId),
    )
    if (components.some((field) => !field)) {
        return 'skip'
    }

    const states = components.map((field) =>
        toCollectionState(field!.enabled, field!.required),
    )
    if (states.every((state) => state === 'skip')) return 'skip'
    if (states.every((state) => state === 'required')) return 'required'
    if (states.every((state) => state === 'optional' || state === 'required')) {
        return states.includes('optional') ? 'optional' : 'required'
    }
    return 'optional'
}

export function applyAddressCollectionState(
    fields: WorkbookFieldConfig[],
    state: CollectionState,
): WorkbookFieldConfig[] {
    const patch = fromCollectionState(state)
    return fields.map((field) =>
        (ADDRESS_COMPONENT_FIELD_IDS as readonly string[]).includes(
            field.field_id,
        )
            ? { ...field, ...patch }
            : field,
    )
}

export function builderFieldsForBucket(
    bucketId: WorkbookBucketId,
): BuilderFieldEntry[] {
    if (
        bucketId !== 'identity_contact' &&
        bucketId !== 'lease_preferences' &&
        bucketId !== 'financial' &&
        bucketId !== 'operational' &&
        bucketId !== 'lifestyle'
    ) {
        return []
    }
    const catalog = catalogFieldsForBucket(bucketId)
    const result: BuilderFieldEntry[] = []
    let addressInserted = false

    for (const field of catalog) {
        if (
            (ADDRESS_COMPONENT_FIELD_IDS as readonly string[]).includes(
                field.id,
            )
        ) {
            if (!addressInserted) {
                result.push(ADDRESS_BUILDER_FIELD)
                addressInserted = true
            }
            continue
        }
        result.push(field)
    }

    return result
}

/** Prefer fields actually placed on the section (supports moves + custom). */
export function builderFieldsForBucketConfig(
    bucket: WorkbookBucketConfig,
): BuilderFieldEntry[] {
    const result: BuilderFieldEntry[] = []
    let addressInserted = false

    for (const field of bucket.fields) {
        if (
            (ADDRESS_COMPONENT_FIELD_IDS as readonly string[]).includes(
                field.field_id,
            )
        ) {
            if (!addressInserted) {
                result.push(ADDRESS_BUILDER_FIELD)
                addressInserted = true
            }
            continue
        }
        const catalog = WORKBOOK_FIELD_CATALOG.find(
            (c) => c.id === field.field_id,
        )
        if (catalog) {
            result.push(catalog)
            continue
        }
        result.push({
            id: field.field_id,
            label: field.label || field.question_text || field.field_id,
            input_type: field.input_type || 'text',
            fha_tier: 'safe',
        })
    }

    return result
}

export function getBuilderFieldCollectionState(
    bucket: WorkbookBucketConfig,
    builderField: BuilderFieldEntry,
): CollectionState {
    if (builderField.componentFieldIds?.length) {
        return getAddressCollectionState(bucket.fields)
    }
    const field = bucket.fields.find(
        (item) => item.field_id === builderField.id,
    )
    if (!field) return 'skip'
    return toCollectionState(field.enabled, field.required)
}

export function applyBuilderFieldCollectionState(
    bucket: WorkbookBucketConfig,
    builderField: BuilderFieldEntry,
    state: CollectionState,
): WorkbookBucketConfig {
    if (builderField.componentFieldIds?.length) {
        return {
            ...bucket,
            fields: applyAddressCollectionState(bucket.fields, state),
        }
    }

    const patch = fromCollectionState(state)
    return {
        ...bucket,
        fields: bucket.fields.map((field) =>
            field.field_id === builderField.id ? { ...field, ...patch } : field,
        ),
    }
}

export function countBuilderBucketFields(
    bucket: WorkbookBucketConfig,
): {
    enabledCount: number
    requiredCount: number
} {
    const builderFields = builderFieldsForBucketConfig(bucket)
    let enabledCount = 0
    let requiredCount = 0

    for (const builderField of builderFields) {
        const state = getBuilderFieldCollectionState(bucket, builderField)
        if (state !== 'skip') enabledCount += 1
        if (state === 'required') requiredCount += 1
    }

    return { enabledCount, requiredCount }
}

export function isAddressComponentFieldId(fieldId: string): boolean {
    return (ADDRESS_COMPONENT_FIELD_IDS as readonly string[]).includes(fieldId)
}

export function formatAddressDisplay(answers: Record<string, unknown>): string {
    const line1 = String(answers.current_address_line1 ?? '').trim()
    const city = String(answers.current_address_city ?? '').trim()
    const state = String(answers.current_address_state ?? '').trim()
    const zip = String(answers.current_address_zip ?? '').trim()

    const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ')
    return [line1, cityStateZip].filter(Boolean).join(', ')
}
